import React, { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAccess } from "../context/AccessContext";
import { useCart } from "../context/CartContext";

export default function BrushId() {
  const { brushIdUnlocked, bidToken } = useAccess();
  const { addItem } = useCart();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const buyAndGo = (id) => { addItem(id); navigate("/cart"); };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError("Image is too large. Please use one under 5MB.");
      return;
    }
    setError(null); setResult(null);
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const identify = async () => {
    if (!preview) return;
    setLoading(true); setError(null); setResult(null);
    try {
      const { data } = await api.post("/brush-id/identify", {
        access_token: bidToken,
        image_base64: preview,
      });
      setResult(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't identify the brush. Try a clearer photo.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => { setPreview(null); setResult(null); setError(null); if (fileRef.current) fileRef.current.value = ""; };

  return (
    <div data-testid="brush-id-page" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
      <div className="overline mb-4">The Studio · AI</div>
      <h1 className="font-serif-display text-5xl sm:text-6xl font-light text-stone-900 tracking-tight">
        Identify any brush.
      </h1>
      <p className="mt-5 max-w-2xl text-stone-600 leading-relaxed">
        Snap a photo of any makeup brush — ours, yours, a thrifted mystery — and our AI tells you exactly what it is, what it's for, and how to use it.
      </p>

      {!brushIdUnlocked ? (
        <div data-testid="brush-id-paywall" className="mt-10 bg-[#2C2A29] text-stone-100 p-8 lg:p-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <div>
            <div className="overline mb-3" style={{ color: "#E8D5D1" }}>Members Only</div>
            <h2 className="font-serif-display text-3xl sm:text-4xl font-light leading-tight">
              $9.99 once. Unlimited forever.
            </h2>
            <p className="mt-4 text-sm text-stone-300 leading-relaxed max-w-md">
              One-time payment, lifetime access on this device. No subscriptions, no per-use fees.
            </p>
          </div>
          <div className="bg-[#F9F7F3] text-stone-900 p-6">
            <div className="overline mb-2">Lifetime Membership</div>
            <div className="font-serif-display text-2xl">AI Brush ID</div>
            <p className="text-xs text-stone-500 mt-1">Unlimited brush identifications, forever.</p>
            <div className="font-serif-display text-4xl mt-4">$9.99</div>
            <button
              data-testid="brush-id-buy-membership"
              onClick={() => buyAndGo("brush-id-pass")}
              className="mt-5 w-full text-xs uppercase tracking-[0.22em] bg-[#8C6A5D] hover:bg-[#70544A] text-white px-7 py-4 transition-colors"
            >
              Get Lifetime Access
            </button>
            <p className="text-xs text-stone-500 mt-3">Also included with the Tutorial Vault ($29) and the Complete 30-Piece Set ($299).</p>
          </div>
        </div>
      ) : (
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          <div className="lg:col-span-6">
            <div className="overline mb-3">Step 01</div>
            <h2 className="font-serif-display text-3xl text-stone-900 font-light">Upload a photo.</h2>
            <p className="mt-2 text-sm text-stone-600">Clear, single-brush photos work best. JPG or PNG, under 5MB.</p>

            <div className="mt-6 border border-dashed border-[#E5E0D8] bg-white aspect-[4/3] flex items-center justify-center overflow-hidden">
              {preview ? (
                <img src={preview} alt="Your brush" className="w-full h-full object-contain" />
              ) : (
                <div className="text-center text-stone-500 px-6">
                  <div className="overline">Drop or select</div>
                  <p className="text-sm mt-1">PNG / JPG · up to 5MB</p>
                </div>
              )}
            </div>

            <input
              ref={fileRef}
              data-testid="brush-id-file"
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              onChange={onFile}
              className="hidden"
            />
            <div className="mt-4 flex gap-3 flex-wrap">
              <button
                data-testid="brush-id-pick"
                onClick={() => fileRef.current?.click()}
                className="text-xs uppercase tracking-[0.22em] border border-[#8C6A5D] text-[#8C6A5D] hover:bg-[#F2EFEB] px-5 py-3 transition-colors"
              >
                {preview ? "Choose Different Photo" : "Choose Photo"}
              </button>
              {preview && (
                <>
                  <button
                    data-testid="brush-id-identify"
                    onClick={identify}
                    disabled={loading}
                    className="text-xs uppercase tracking-[0.22em] bg-[#8C6A5D] hover:bg-[#70544A] disabled:bg-stone-300 text-white px-5 py-3 transition-colors"
                  >
                    {loading ? "Analyzing…" : "Identify Brush"}
                  </button>
                  <button
                    onClick={reset}
                    className="text-xs uppercase tracking-[0.22em] text-stone-500 hover:text-stone-900 px-2 py-3"
                  >
                    Reset
                  </button>
                </>
              )}
            </div>
            {error && <p data-testid="brush-id-error" className="mt-4 text-sm text-red-700">{error}</p>}
          </div>

          <div className="lg:col-span-6">
            <div className="overline mb-3">Step 02</div>
            <h2 className="font-serif-display text-3xl text-stone-900 font-light">Get the answer.</h2>

            {!result && !loading && (
              <p className="mt-2 text-sm text-stone-500 italic">Upload a photo and tap Identify Brush.</p>
            )}
            {loading && (
              <div className="mt-6 bg-[#F2EFEB] p-6 animate-pulse h-40" />
            )}
            {result && (
              <div data-testid="brush-id-result" className="mt-6 bg-white border border-[#E5E0D8] p-6">
                <div className="overline mb-2">
                  Match · {result.confidence ? result.confidence.toUpperCase() : "—"} confidence
                </div>
                <h3 className="font-serif-display text-3xl text-stone-900 leading-tight">
                  {result.name || "Generic Brush"}
                </h3>

                <div className="mt-5">
                  <div className="overline mb-2">Used for</div>
                  <p className="text-sm text-stone-700 leading-relaxed">{result.used_for}</p>
                </div>

                {result.how_to_use_short && (
                  <div className="mt-5">
                    <div className="overline mb-2">How to use</div>
                    <p className="text-sm text-stone-700 leading-relaxed">{result.how_to_use_short}</p>
                  </div>
                )}

                {result.notes && (
                  <div className="mt-5 bg-[#F2EFEB] p-4">
                    <div className="overline mb-1">Notes</div>
                    <p className="text-xs text-stone-700 italic leading-relaxed">{result.notes}</p>
                  </div>
                )}

                {result.catalog_url && (
                  <Link
                    to={result.catalog_url}
                    className="inline-block mt-6 text-xs uppercase tracking-[0.22em] bg-[#8C6A5D] hover:bg-[#70544A] text-white px-5 py-3 transition-colors"
                  >
                    See full tutorial →
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
